import { useCallback, useEffect, useState } from 'react';
import {
  type EventsOverview,
  type LifecycleOverview,
  SIGNUP_COUNTING_FROM,
  type SignupOverview,
  type UsageOverview,
  type WorkspaceUsageRow,
} from '@floorpoll/shared';
import { AdoptionBars } from '../../components/AdoptionBars.js';
import { formatBuildStamp } from '../../components/AppVersion.js';
import { EventDayStrip } from '../../components/EventDayStrip.js';
import { FormError } from '../../components/Form.js';
import { Ledger } from '../../components/Ledger.js';
import { StatTile } from '../../components/StatTile.js';
import { UsageColumns } from '../../components/UsageColumns.js';
import { WorkspaceUsageTable } from '../../components/WorkspaceUsageTable.js';
import { config } from '../../config.js';
import { ApiError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { usageApi } from '../../lib/usageApi.js';

const WINDOWS = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 3650, label: 'All time' },
];

/**
 * Spans for the daily chart, kept apart from WINDOWS above.
 *
 * The KPI toggle offers "All time" because a cumulative total has one. A daily
 * series does not: 3650 columns is a grey block, and the API clamps the range
 * to 120 days for that reason. Two different questions, two different controls.
 */
const EVENT_SPANS = [
  { days: 14, label: '14d' },
  { days: 28, label: '28d' },
  { days: 56, label: '56d' },
];

/**
 * The two series the day strip and the ledger draw. Both forms of each noun,
 * because a one-day column and a single-signup channel are both common enough
 * that "1 signups" would be a routine reading rather than an edge case.
 */
const ACTION_UNIT = { one: 'action', many: 'actions' };
const SIGNUP_UNIT = { one: 'signup', many: 'signups' };

export function Usage() {
  const { platformAdmin } = useAuth();
  const [overview, setOverview] = useState<UsageOverview | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceUsageRow[]>([]);
  const [windowDays, setWindowDays] = useState(90);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<EventsOverview | null>(null);
  const [eventDays, setEventDays] = useState(28);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [planBusy, setPlanBusy] = useState<string | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const [signups, setSignups] = useState<SignupOverview | null>(null);
  const [signupsError, setSignupsError] = useState<string | null>(null);
  const [signupsLoading, setSignupsLoading] = useState(true);
  const [lifecycle, setLifecycle] = useState<LifecycleOverview | null>(null);
  const [lifecycleError, setLifecycleError] = useState<string | null>(null);
  const [lifecycleLoading, setLifecycleLoading] = useState(true);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [nextOverview, nextWorkspaces] = await Promise.all([
        usageApi.overview(windowDays),
        usageApi.workspaces(),
      ]);
      setOverview(nextOverview);
      setWorkspaces(nextWorkspaces);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load usage.');
    }
  }, [windowDays]);

  /**
   * Applies a grant change and patches the one row it affected.
   *
   * The server's answer replaces the row's plan rather than the click's
   * intention, because the two can differ: granting a workspace that already
   * has a live subscription still stores `source: 'grant'` — a grant
   * outranks any real renewal date in `maxIso` — so the plan that comes back
   * is `granted`, not the `marquee` the click implied. That value is the
   * write's own answer, not a fresh read; see `grantPlan`/`revokePlan` in
   * usageApi.
   *
   * Reports failure through `planError`, not the page-wide `setError` — same
   * reasoning as `loadEvents` below: a failed click is not a reason to blank
   * the KPIs and the table, and the table is exactly what a 409's "try
   * again" requires still be on screen.
   */
  const setPlan = useCallback(async (workspaceId: string, granting: boolean) => {
    setPlanError(null);
    setPlanBusy(workspaceId);
    try {
      const plan = granting
        ? await usageApi.grantPlan(workspaceId)
        : await usageApi.revokePlan(workspaceId);
      setWorkspaces((rows) =>
        rows.map((row) => (row.workspaceId === workspaceId ? { ...row, plan } : row)),
      );
    } catch (caught) {
      setPlanError(caught instanceof ApiError ? caught.message : 'Could not change that plan.');
    } finally {
      setPlanBusy(null);
    }
  }, []);

  // Its own effect, keyed on its own span: changing the daily range must not
  // re-fetch the workspace table, and a failure here must not blank the page —
  // hence a panel-local error rather than the page-wide `setError` its sibling
  // uses, which would replace the KPIs and the workspace table with a banner.
  //
  // A failure deliberately leaves `events` alone. Clearing it would swap an
  // already-correct chart for an empty state, and this panel's empty state
  // makes a checkable claim — "an empty stretch means no one signed in, not
  // that tracking is off" — which a failed request turns into a lie. Stale
  // numbers under an error banner are honest; a confident zero is not.
  const loadEvents = useCallback(async () => {
    setEventsLoading(true);
    setEventsError(null);
    try {
      setEvents(await usageApi.events(eventDays));
    } catch (caught) {
      setEventsError(
        caught instanceof ApiError ? caught.message : 'Could not load console activity.',
      );
    } finally {
      setEventsLoading(false);
    }
  }, [eventDays]);

  useEffect(() => {
    if (platformAdmin) void load();
  }, [platformAdmin, load]);

  useEffect(() => {
    if (platformAdmin) void loadEvents();
  }, [platformAdmin, loadEvents]);

  // Panel-local state throughout, for the reasons spelled out on `loadEvents`:
  // its own error so a failure here does not blank the KPIs, and `signups`
  // left alone on failure so an already-correct chart is never replaced by an
  // empty state that claims nobody signed up.
  const loadSignups = useCallback(async () => {
    setSignupsLoading(true);
    setSignupsError(null);
    try {
      setSignups(await usageApi.signups(eventDays));
    } catch (caught) {
      setSignupsError(
        caught instanceof ApiError ? caught.message : 'Could not load signup sources.',
      );
    } finally {
      setSignupsLoading(false);
    }
  }, [eventDays]);

  useEffect(() => {
    if (platformAdmin) void loadSignups();
  }, [platformAdmin, loadSignups]);

  // Shares the daily-range control with console activity: both answer "what
  // happened over these days", and two range pickers on one page would be a
  // question nobody asked.
  const loadLifecycle = useCallback(async () => {
    setLifecycleError(null);
    setLifecycleLoading(true);
    try {
      setLifecycle(await usageApi.lifecycle(eventDays));
    } catch (caught) {
      setLifecycleError(
        caught instanceof ApiError ? caught.message : 'Could not load lifecycle email.',
      );
    } finally {
      setLifecycleLoading(false);
    }
  }, [eventDays]);

  useEffect(() => {
    if (platformAdmin) void loadLifecycle();
  }, [platformAdmin, loadLifecycle]);

  // RequireAuth checks authentication only, never authorization. This guard is
  // cosmetic — the API is the real boundary, and a non-admin never reaches it.
  if (!platformAdmin) {
    return (
      <section>
        <h1>Usage</h1>
        <p className="muted">This page is not available.</p>
      </section>
    );
  }

  if (error) {
    return (
      <section>
        <h1>Usage</h1>
        <p className="muted">{error}</p>
      </section>
    );
  }

  if (!overview) {
    return (
      <section>
        <h1>Usage</h1>
        <p className="muted">Loading…</p>
      </section>
    );
  }

  const windowLabel = WINDOWS.find((w) => w.days === windowDays)?.label ?? '90 days';
  // The tiles sit five to a row, so the delta caption uses the short form —
  // "90 days" wraps the widest tile onto a second line at desktop width.
  const deltaPeriod = windowLabel.replace(' days', 'd').replace('All time', 'all time');
  const delta = (key: keyof UsageOverview['totals']) =>
    overview.totals[key] - overview.previousTotals[key];

  return (
    <section>
      <div className="page-head">
        <div>
          <p className="eyebrow">Operator</p>
          <h1>Usage</h1>
          <p className="muted">
            All workspaces
            {overview.liveNow > 0 ? (
              <>
                {' · '}
                <span className="lamp" />
                {overview.liveNow} live now
              </>
            ) : null}
          </p>
          <p className="app-version">{formatBuildStamp(config.appVersion, config.buildTime)}</p>
        </div>
        <div className="page-head-actions" role="group" aria-label="Time range">
          {WINDOWS.map((option) => (
            <button
              key={option.days}
              type="button"
              className={option.days === windowDays ? undefined : 'secondary'}
              aria-pressed={option.days === windowDays}
              onClick={() => setWindowDays(option.days)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="usage-kpis">
        <StatTile
          label="Users"
          value={overview.totals.users}
          delta={delta('users')}
          deltaPeriod={deltaPeriod}
        />
        <StatTile
          label="Workspaces"
          value={overview.totals.workspaces}
          delta={delta('workspaces')}
          deltaPeriod={deltaPeriod}
        />
        <StatTile
          label="Polls"
          value={overview.totals.quizzes}
          delta={delta('quizzes')}
          deltaPeriod={deltaPeriod}
        />
        <StatTile
          label="Sessions run"
          value={overview.totals.sessionsStarted}
          delta={delta('sessionsStarted')}
          deltaPeriod={deltaPeriod}
        />
        <StatTile
          label="Votes in sessions"
          value={overview.totals.votesInSessions}
          delta={delta('votesInSessions')}
          deltaPeriod={deltaPeriod}
        />
      </div>

      <div className="usage-charts">
        <UsageColumns
          points={overview.signupsPerWeek}
          title="Signups per week"
          caption="New accounts, last 12 weeks"
          unit="signups"
        />
        <UsageColumns
          points={overview.sessionsPerWeek}
          title="Sessions run per week"
          caption="Polls actually presented to a room"
          unit="sessions"
        />
      </div>

      <div className="usage-split">
        <div className="panel">
          <h2 className="panel-title">Active users</h2>
          <p className="panel-caption">Signed into the console</p>
          <div className="usage-actives">
            <div className="usage-active-row">
              <span className="muted">Today</span>
              <strong>{overview.actives.today}</strong>
            </div>
            <div className="usage-active-row">
              <span className="muted">This week</span>
              <strong>{overview.actives.thisWeek}</strong>
            </div>
            <div className="usage-active-row">
              <span className="muted">This month</span>
              <strong>{overview.actives.thisMonth}</strong>
            </div>
            <div className="usage-active-row">
              {/*
                The window is calendar months, not a rolling 30 days, so it
                spans anywhere from 30 to 62 days depending on when the page is
                loaded. Said here because the number is meaningless without it.
              */}
              <span className="muted">Dormant (this month and last)</span>
              <strong>{overview.actives.dormant}</strong>
            </div>
          </div>
        </div>

        <div className="panel">
          <h2 className="panel-title">Feature adoption</h2>
          <p className="panel-caption">
            Share of polls or workspaces using each feature. “Ever presented” only counts sessions
            recorded since usage tracking shipped.
          </p>
          <AdoptionBars adoption={overview.adoption} />
        </div>
      </div>

      <div className="panel activity-panel">
        <div className="activity-head">
          <div>
            <h2 className="panel-title">Console activity</h2>
            <p className="panel-caption">
              What signed-in people did, counted across every workspace. Anonymous voters are not
              included.
            </p>
          </div>
          <div className="page-head-actions" role="group" aria-label="Daily range">
            {EVENT_SPANS.map((span) => (
              <button
                key={span.days}
                type="button"
                className={span.days === eventDays ? undefined : 'secondary'}
                aria-pressed={span.days === eventDays}
                onClick={() => setEventDays(span.days)}
              >
                {span.label}
              </button>
            ))}
          </div>
        </div>

        <FormError message={eventsError} />

        {/*
          Three states, not two. `events` alone cannot tell them apart — before
          this it was null while loading, null on failure and null-ish when
          genuinely empty, so the panel confidently reported "nothing counted"
          during both a first paint and an outage.
        */}
        {events ? (
          events.total === 0 ? (
            <p className="muted">
              Nothing counted in the last {events.windowDays} days. Counting starts when someone
              opens the console — an empty stretch here means no one signed in, not that tracking is
              off.
            </p>
          ) : (
            <>
              <p className="activity-summary">
                <strong>{events.total.toLocaleString()}</strong> actions ·{' '}
                <strong>{events.averagePerDay.toLocaleString()}</strong> a day on average
                {events.busiestDay ? (
                  <>
                    {' · busiest '}
                    <strong>{events.busiestDay.count.toLocaleString()}</strong> on{' '}
                    {events.busiestDay.day}
                  </>
                ) : null}
              </p>

              <EventDayStrip
                points={events.perDay}
                label="Console actions per day"
                unit={ACTION_UNIT}
              />

              <h3 className="activity-subtitle">What they clicked</h3>
              <Ledger rows={events.breakdown} unit={ACTION_UNIT} />
            </>
          )
        ) : eventsLoading ? (
          <p className="muted">Loading…</p>
        ) : null}
      </div>

      <div className="panel activity-panel">
        <h2 className="panel-title">Where signups come from</h2>
        <p className="panel-caption">
          New accounts by the channel their signup link claimed. A source is counted the moment an
          account is created and never stored against it — these rows hold integers and no user id,
          so this panel cannot be narrowed to a person.
        </p>

        <FormError message={signupsError} />

        {/*
          Three states like the two panels around it, plus a fourth thing the
          others do not have to say: this counter started long after the
          product did, so most of a 56-day window is a stretch nothing was
          counted in. Rendering those columns without saying so reports every
          channel as dead on arrival, which is the one claim on this panel that
          can be flatly wrong.
        */}
        {signups ? (
          <>
            {signups.perDay[0] && signups.perDay[0].day < SIGNUP_COUNTING_FROM ? (
              <p className="muted">
                Counting started {SIGNUP_COUNTING_FROM}. Days before that are empty because nothing
                was counted, not because nobody signed up — and for the same reason this total sits
                below “Signups per week” above over any window reaching further back.
              </p>
            ) : null}

            {signups.total === 0 ? (
              <p className="muted">No signups counted in the last {signups.windowDays} days.</p>
            ) : (
              <>
                <p className="activity-summary">
                  <strong>{signups.total.toLocaleString()}</strong> signups
                  {signups.bySource[0] ? (
                    <>
                      {' · led by '}
                      <strong>{signups.bySource[0].label}</strong> at{' '}
                      {signups.bySource[0].percentage}%
                    </>
                  ) : null}
                </p>

                <EventDayStrip points={signups.perDay} label="Signups per day" unit={SIGNUP_UNIT} />

                <h3 className="activity-subtitle">Which channel</h3>
                <Ledger rows={signups.bySource} unit={SIGNUP_UNIT} />
              </>
            )}
          </>
        ) : signupsLoading ? (
          <p className="muted">Loading…</p>
        ) : null}
      </div>

      <div className="panel">
        <h2 className="panel-title">Lifecycle email</h2>
        <p className="panel-caption">
          The new-user sequence, counted across every workspace. Read-only: nothing on this page
          sends, pauses, or resends anything.
        </p>

        <FormError message={lifecycleError} />

        {/*
          Three states like the activity panel above, and for the same reason —
          zero sends and a failed read are different findings, and a panel that
          renders both as an empty table reports the wrong one.
        */}
        {lifecycle ? (
          <>
            <p className="activity-summary">
              <strong>{lifecycle.totals.sent.toLocaleString()}</strong> sent ·{' '}
              <strong>{lifecycle.totals.optOuts.toLocaleString()}</strong> unsubscribed
              {lifecycle.totals.failures > 0 ? (
                <>
                  {' · '}
                  <strong>{lifecycle.totals.failures.toLocaleString()}</strong> failed to send
                </>
              ) : null}
            </p>

            {lifecycle.totals.sent === 0 ? (
              <p className="muted">
                Nothing sent in this window. The sequence is switched on per stage — a stage with no
                lifecycle context creates no schedule at all, so an empty stretch here may mean it
                is off rather than that nobody qualified.
              </p>
            ) : null}

            <ul className="lifecycle-steps">
              {lifecycle.byStep.map((step) => (
                <li key={step.id}>
                  <span>{step.label}</span>
                  <strong>{step.sent.toLocaleString()}</strong>
                </li>
              ))}
            </ul>
          </>
        ) : lifecycleLoading ? (
          <p className="muted">Loading…</p>
        ) : null}
      </div>

      <FormError message={planError} />
      <WorkspaceUsageTable
        workspaces={workspaces}
        // Both or neither: the component takes one `actions` object precisely so a
        // half-wired table — Revoke buttons rendered but inert — cannot be written.
        actions={{
          grant: (workspaceId) => void setPlan(workspaceId, true),
          revoke: (workspaceId) => void setPlan(workspaceId, false),
        }}
        busyWorkspaceId={planBusy}
      />
    </section>
  );
}
